import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { ReportingService } from '../services/reporting/reporting.service';
import { DayReport } from '../models/dayreport';
import { Observable, Subscription } from 'rxjs';

import { Week } from '../models/week';
import { PrepareListData } from '../helpers/preplistdata';
import { ReportSummaries } from '../models/reportsummaries';
import { ReportGroup } from '../models/reportgroup';
import { GroupAdminService } from '../services/groupadmin/groupadmin.service';
import { ReturnObject } from '../models/returnObject';
import { InternalMsgService } from '../services/internal.msg.service';
import { AccountService } from '../services/account.service';
import { AccountItem } from '../models/accountitem';
import { Sort } from '../models/sort';
import { StatusMessagesService } from '../services/statusmessage.service';

@Component({
  selector: 'group-period-report',
  templateUrl: './groupperiodreport.component.html',
  styleUrls: ['./groupperiodreport.component.css'],
  providers: [],
})
export class GroupPeriodReportComponent implements OnInit, OnDestroy {
    @Input() periodType = '';

    fromDate: string = '';
    msgSubscr: Subscription = new Subscription;

    filteredWeeks: Observable<Week[]> = new Observable<Week[]>();
    dataSource: DayReport[] = [];
    weekList: Week[] = [];
    reportGroups: ReportGroup[] = [new ReportGroup()];
    
    loggedAccount: AccountItem = new AccountItem;
    summary: ReportSummaries = new ReportSummaries();

    selectedGroup: ReportGroup = new ReportGroup();
    selectedWeek: string = '';
    sortField: string = 'empName';

    reportTotalHours: number = 0;
    rowCount: number = 0;

    constructor(private reportingService: ReportingService,
                private groupAdminService: GroupAdminService,
                private msgService: InternalMsgService,
                private statusMessagesService: StatusMessagesService,
                private accountService: AccountService) { }

    // ----------------------------------------------------------------------------------
    // -- Life cycle hooks
    // ----------------------------------------------------------------------------------
    ngOnInit(): void {
        const today = new Date();
        this.fromDate = today.toISOString().toString().split('T')[0];

        
        window.addEventListener("tdsSort", (event: any) => {
            const details = event['detail'];
            const sortObj: Sort = {
                active: details['columnKey'],
                direction: details['sortingDirection']
            }
            this.sort(sortObj);
        });


        this.msgSubscr = this.msgService.loggedInAccountSet$.subscribe( 
            account => {
                this.loggedAccount = account;
                if (this.periodType == 'week') {
                    this.getWeekList();
                }
                this.getReportGroupData();
        } );     
        this.accountService.fetchAccount();
    }

    ngOnDestroy(): void {
        this.msgSubscr.unsubscribe();
    }

    // ----------------------------------------------------------------------------------
    // -- HTTP --------------------------------------------------------------------------
    // ----------------------------------------------------------------------------------
    async getMonthData(): Promise<void> {
        this.msgService.ontoggleSpinnerSet(true);
        const fromDate: Date = new Date( this.fromDate );
        fromDate.setHours(11);

        const yearMonthString = this.fromDate.substring( 0, 7 );

        this.reportingService.getGroupMonthReportData(this.loggedAccount.email, yearMonthString, this.selectedGroup.groupId)
            .subscribe( { 
                next: (data: ReturnObject<DayReport[]>) => {
                    if (data.isFailure) {
                       this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    PrepareListData.prepareData(data.value, this.summary, this.sortField).then( data => {
                        this.dataSource = !data
                            ? []
                            : data;
                        this.countHours();
                        this.countRows();
                    });
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getWeekList(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.reportingService.getReportedWeekstData()
            .subscribe( { 
                next: (data: ReturnObject<Week[]>) => {
                    if (data.isFailure) {
                       this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.weekList = data.value;
                    this.weekList.forEach( x => {
                        const dates = `${x.firstDayOfWeek.split('T')[0]} - ${x.lastDayOfWeek.split('T')[0]}`;
                        const weekNo = `${x.weekNo.slice(0, 4)}-${x.weekNo.slice(4, 6)}`;
                        x.weekNo = weekNo;
                        x.displayWeek =  `${weekNo} (${dates})`;
                    });                
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getWeekData(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.reportingService.getGroupWeekdReportData( this.loggedAccount.email, this.selectedWeek, this.selectedGroup.groupId )
            .subscribe( { 
                next: (data: ReturnObject<DayReport[]>) => {
                    if (data.isFailure) {
                       this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    PrepareListData.prepareData(data.value, this.summary, this.sortField).then( data => {
                        this.dataSource = !data
                            ? []
                            : data;
                        this.countHours();
                        this.countRows();
                    });
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getReportGroupData(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.groupAdminService.get(this.loggedAccount.email)
            .subscribe( { 
                next: (data: ReturnObject<ReportGroup[]>) => {
                    if (data.isFailure) {
                       this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    if (data.value.length > 0) {
                        this.reportGroups = data.value;
                    }
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    
    // ----------------------------------------------------------------------------------
    // -- LOGIC
    // ----------------------------------------------------------------------------------
    countHours(): void {
        this.reportTotalHours = 0;
        if (this.dataSource && this.dataSource.length > 0) {
            for (let days of this.dataSource) {
                if (days.isSubTotalRow) {
                    this.reportTotalHours += days.hours;
                }
            }
        }
    }

    countRows(): void {
        this.rowCount = 0;
        if (this.dataSource && this.dataSource.length > 0) {
            for (let days of this.dataSource) {
                if (!days.isSubTotalRow) {
                    this.rowCount += 1;
                }
            }
        }
    }

    getReportData(): void {
       if (this.periodType == 'month') {
            this.getMonthData();
            return;
        }

        if (this.periodType == 'week') {
            this.getWeekData();
            return;
        }
    }

    private filterWeeks(searchText: string): Week[] {
        return this.weekList.filter(week => {
            if (typeof searchText !== 'string') {
                return false;
            }
            const input = searchText.toLowerCase();
            return week.weekNo !== null
                ? week.weekNo.indexOf(input) >= 0 || week.weekNo.indexOf(input) >= 0
                : false;
        });
    }

    selectWeek(week: Week): void {
        this.selectedWeek = week.weekNo;
        const dates = `${week.firstDayOfWeek.split('T')[0]} - ${week.lastDayOfWeek.split('T')[0]}`;
    }

    async sort(sort: Sort) {
        if (this.dataSource.length === 0) return;
        PrepareListData.sort(this.dataSource, sort, this.sortField ).then( data => {
            this.dataSource = data; 
        });
    }

    isSubmitDisabled(): boolean {
        return this.periodType == 'month' && this.selectedGroup.groupId == -1
        ||this.periodType == 'month' && this.fromDate == ''
        || this.periodType == 'week' && this.selectedGroup.groupId == -1
        || this.periodType == 'week' && this.selectedWeek == '';
    }

    setSelectedGroup(group: ReportGroup): void {
        this.selectedGroup = group;
    }
}

